// ---------------------------------------------------------------------------
// The arithmetic behind the timeline, away from the component.
//
// Separated because this is the part that can be wrong without looking wrong: a
// track of blocks always renders, and blocks of the wrong width still look like
// a timeline. Nobody notices that a forty-second line and a four-second line
// are drawn the same size — they notice a year later when they wonder why the
// middle of the video feels long.
// ---------------------------------------------------------------------------

/** Per-line seconds, defaulting a missing or non-positive value to one. */
export function secondsPerLine(count: number, lineSeconds: readonly (number | undefined)[]): number[] {
  return Array.from({ length: count }, (_, i) => {
    const n = lineSeconds[i];
    return typeof n === 'number' && isFinite(n) && n > 0 ? n : 1;
  });
}

/**
 * Block widths as percentages, proportional to seconds but never below a floor.
 *
 * The floor is what makes a two-word line clickable and draggable. Getting the
 * two rules to hold together is the whole difficulty, and the obvious version
 * does not: lift everything below the floor, then rescale so the total is 100,
 * and the rescale pushes those blocks straight back under it —
 *
 *     [600, 1, 1, 1] with a floor of 4  ->  [89.2, 3.59, 3.59, 3.59]
 *
 * which is a track that looks completely correct and still has three slivers
 * nobody can grab. Caught by the test, not by reading it.
 *
 * So the floor is PINNED instead. Anything below it is fixed there, the space
 * that remains is shared out among the rest in proportion, and that repeats
 * because paying for one floor can push another block under it. Blocks that
 * never hit the floor keep their exact ratios — a line twice as long stays
 * twice as wide — and the widths always sum to 100, so nothing is clipped off
 * the end of the track.
 */
export function trackWidths(seconds: readonly number[], minPercent: number): number[] {
  const n = seconds.length;
  if (!n) return [];
  const total = seconds.reduce((a, b) => a + b, 0);
  // Every line degenerate, or so many blocks that the floors alone overflow the
  // track: an even split beats dividing by zero or overflowing silently.
  if (!(total > 0) || minPercent * n >= 100) return seconds.map(() => 100 / n);

  const pinned = new Array<boolean>(n).fill(false);
  const width = new Array<number>(n).fill(0);

  for (;;) {
    const free = seconds.reduce((a, s, i) => a + (pinned[i] ? 0 : s), 0);
    const room = 100 - seconds.reduce((a, _, i) => a + (pinned[i] ? minPercent : 0), 0);
    let changed = false;
    for (let i = 0; i < n; i++) {
      if (pinned[i]) { width[i] = minPercent; continue; }
      width[i] = free > 0 ? (seconds[i]! / free) * room : room / n;
      if (width[i]! < minPercent) { pinned[i] = true; changed = true; }
    }
    if (!changed) return width;
  }
}

/** Where each line starts, in seconds from the top of the programme. */
export function startOffsets(seconds: readonly number[]): number[] {
  const out: number[] = [];
  let elapsed = 0;
  for (const s of seconds) { out.push(elapsed); elapsed += s; }
  return out;
}

export function mmss(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** A layer as the API stores it: anchored to a scene, not to a clock. */
export interface StoredLayer {
  id: string;
  track: string;
  anchor_id: string;
  offset_s: number | string;
  seconds: number | string;
  props: { text?: string; position?: string; style?: string; size?: number; font?: string;
           bold?: boolean; italic?: boolean; underline?: boolean; color?: string;
           sub?: string; url?: string; assetId?: string; height?: number;
           gainDb?: number; fadeIn?: number; fadeOut?: number } | null;
}

/** The same layer once its anchor has been resolved to a moment. */
export interface PlacedLayer extends StoredLayer {
  start: number;
  end: number;
}

/**
 * Where each scene begins, by scene id.
 *
 * The same rule the render plane uses — a scene starts after the sum of the
 * holds before it. It is duplicated here deliberately and narrowly: the studio
 * has to draw something before a render exists, and it cannot ask the renderer
 * where a layer will land. What keeps the two honest is that both read the same
 * per-line seconds, which the API sends measured when a render has produced
 * them and estimated otherwise.
 */
export function sceneStarts(
  ids: readonly (string | undefined)[],
  seconds: readonly number[],
): Map<string, number> {
  const out = new Map<string, number>();
  let at = 0;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    if (id) out.set(id, at);
    at += seconds[i] ?? 0;
  }
  return out;
}

/**
 * Layers placed against their scenes, in time order.
 *
 * A layer whose scene is gone is dropped, exactly as the renderer drops it —
 * the thing it annotated no longer exists, and drawing it somewhere else would
 * invent a placement nobody chose.
 */
export function placeLayers(
  layers: readonly StoredLayer[],
  starts: Map<string, number>,
): PlacedLayer[] {
  const out: PlacedLayer[] = [];
  for (const l of layers) {
    const base = starts.get(l.anchor_id);
    if (base === undefined) continue;
    const offset = Number(l.offset_s);
    const seconds = Number(l.seconds);
    if (!isFinite(offset) || !isFinite(seconds) || seconds <= 0 || offset < 0) continue;
    out.push({ ...l, start: base + offset, end: base + offset + seconds });
  }
  return out.sort((a, b) => a.start - b.start);
}

/**
 * Where to put the ticks on a timeline of `total` seconds.
 *
 * A ruler whose marks land on 7s, 14s, 21s is a ruler nobody reads. The step
 * is chosen from a fixed ladder of intervals people actually count in, so the
 * labels are 0:15, 0:30, 0:45 rather than whatever divides the programme
 * evenly — the point of a ruler is that the numbers on it are ones you already
 * know how to think in.
 *
 * Returns seconds, ascending, never including `total` itself: a mark flush
 * against the right edge has no room for its label and reads as an off-by-one.
 */
const STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800] as const;

export function rulerMarks(total: number, target = 8): number[] {
  if (!(total > 0) || !Number.isFinite(total)) return [];
  // The first step that keeps the count at or under `target`. Falling off the
  // end means a programme longer than the widest step, where the widest is
  // still the right answer.
  const step = STEPS.find((s) => total / s <= target) ?? STEPS[STEPS.length - 1]!;
  const out: number[] = [];
  for (let at = step; at < total; at += step) out.push(at);
  return out;
}


/** The scene the playhead is in: the last one that starts at or before `t`. */
export function sceneAt(starts: readonly number[], seconds: readonly number[], t: number): number {
  if (!starts.length) return 0;
  let at = 0;
  for (let i = 0; i < starts.length; i++) {
    if ((starts[i] ?? 0) <= t) at = i;
  }
  // Past the end is the last scene, not one beyond it.
  const last = starts.length - 1;
  const end = (starts[last] ?? 0) + (seconds[last] ?? 0);
  return t >= end ? last : at;
}

export interface Join { kind: string; seconds: number }
export interface JoinAt { from: number; to: number; kind: string; seconds: number; progress: number }

/**
 * The join the playhead is inside, or null.
 *
 * The same clock the renderer keeps: a crossfade BEGINS where the next scene
 * starts and runs for its length, so the window for the join into scene i+1
 * is [start(i+1), start(i+1) + seconds). A cut has no window — the picture
 * changes on the boundary and there is nothing to be inside.
 */
export function joinAt(starts: readonly number[], joins: readonly Join[], t: number): JoinAt | null {
  for (let i = 0; i < joins.length && i + 1 < starts.length; i++) {
    const j = joins[i]!;
    if (j.kind === 'cut' || j.seconds <= 0) continue;
    const begin = starts[i + 1] ?? 0;
    if (t >= begin && t < begin + j.seconds) {
      return { from: i, to: i + 1, kind: j.kind, seconds: j.seconds, progress: (t - begin) / j.seconds };
    }
  }
  return null;
}

/** The layers on screen at `t`, in the order they start. */
export function layersAt<T extends { start: number; end: number }>(placed: readonly T[], t: number): T[] {
  return placed.filter((l) => l.start <= t && t < l.end);
}


/**
 * A block's new length after its right edge is dragged `dx` pixels along a
 * track `trackPx` wide that shows `total` seconds. Never below `min`, and
 * rounded to a tenth, because a banner that is 3.27s long is not a decision
 * anybody made.
 */
export function draggedSeconds(seconds: number, dx: number, trackPx: number, total: number, min = 0.5): number {
  if (trackPx <= 0 || total <= 0) return seconds;
  const next = seconds + (dx / trackPx) * total;
  return Math.max(min, Math.round(next * 10) / 10);
}
